import type { Website } from './../index.d.ts'

/**
 * Composable for managing website-related functionality and state.
 * Provides computed properties and methods to determine current website context.
 *
 * @returns {Object} An object containing website-related properties and methods
 * @property {ComputedRef<Website>} currentWebsite - The current active website
 * @property {Ref<Website>} deferredCurrentWebsite - The deferred current website state
 * @property {ComputedRef<boolean>} isGlobalWebsite - Indicates if the current website is global
 * @property {ComputedRef<boolean>} isDeferredGlobalWebsite - Indicates if the deferred website is global
 * @property {ComputedRef<boolean>} isGiorgioArmaniWebsite - Indicates if the current website is Giorgio Armani
 * @property {ComputedRef<boolean>} isEmporioArmaniWebsite - Indicates if the current website is Emporio Armani
 * @property {ComputedRef<boolean>} isArmaniExchangeWebsite - Indicates if the current website is Armani Exchange
 * @property {ComputedRef<boolean>} isEa7Website - Indicates if the current website is EA7
 * @property {ComputedRef<boolean>} isBookAppointmentWebsite - Indicates if the current website supports book appointment
 * @property {Function} isBrand - Method to check if a given website is a valid brand
 * @property {Function} viewTypePage - Method to determine the current page view type
 */
export const useWebsite = () => {
  // Computed property for the current website from app configuration
  const currentWebsite = computed(() => useAppConfig().website)

  // Reactive state for the deferred current website
  const deferredCurrentWebsite = useState<Website>('deferred-current-website')

  // Computed property to check if the current website is global
  const isGlobalWebsite = computed(() => currentWebsite.value === 'global')

  // Computed property to check if the deferred website is global
  const isDeferredGlobalWebsite = computed(
    () => deferredCurrentWebsite.value === 'global'
  )

  // Computed property to check if the current website is Giorgio Armani
  const isGiorgioArmaniWebsite = computed(
    () => currentWebsite.value === 'giorgio-armani'
  )

  // Computed property to check if the current website is Emporio Armani
  const isEmporioArmaniWebsite = computed(
    () => currentWebsite.value === 'emporio-armani'
  )

  // Computed property to check if the current website is Armani Exchange
  const isArmaniExchangeWebsite = computed(
    () => currentWebsite.value === 'armani-exchange'
  )

  // Computed property to check if the current website is EA7
  const isEa7Website = computed(() => currentWebsite.value === 'ea7')

  // Computed property to check if the current website supports book appointment
  const isBookAppointmentWebsite = computed(
    () =>
      currentWebsite.value === 'giorgio-armani' ||
      currentWebsite.value === 'emporio-armani' ||
      currentWebsite.value === 'ea7' ||
      currentWebsite.value === 'armani-exchange' ||
      currentWebsite.value === 'armani-casa'
  )

  /**
   * Checks if a given website is a valid brand.
   *
   * @param {string} w - The website to check
   * @returns {boolean} True if the website is a valid brand, false otherwise
   */
  const isBrand = (w: string) => (brands as string[]).includes(w)

  /**
   * Determines the current page view type.
   *
   * @returns {string | null} The view type of the current page, or null if no match
   */
  const viewTypePage = () => {
    let viewType = ''

    switch (true) {
      case viewType === 'product':
        return (viewType = 'pdp_page')
      case viewType === 'categorylvl1':
        return (viewType = 'plp_page')
      case viewType === 'wishlist':
        return (viewType = 'wishlist_page')
      case viewType === 'cart':
        return (viewType = 'cart_page')
      default:
        return null
    }
  }

  return {
    currentWebsite,
    deferredCurrentWebsite,
    isGlobalWebsite,
    isDeferredGlobalWebsite,
    isGiorgioArmaniWebsite,
    isEmporioArmaniWebsite,
    isArmaniExchangeWebsite,
    isEa7Website,
    isBookAppointmentWebsite,
    isBrand,
    viewTypePage,
  }
}
